import React from 'react';
import { ApolloError } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import {
  IPaginationProps,
  LinearProgress,
  Pagination,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LoadedContent,
} from '@occc-app/design-system';
import { PartialAudienceFragment } from '@occc/graphql';
import getAudienceStatusVariant from '@occc-app/lib/getAudienceStatusVariant';
import { getStatusLabel, hasFixedDateRange, routeCreator } from '@occc-app/routes/Audience/utils';

import { AudienceActionsContainer } from './AudienceActionsContainer';

interface IAudiencesTableProps {
  audiences?: PartialAudienceFragment[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  isUserPermittedToEditAudience: boolean;
}

export const AudiencesTable: React.FC<IAudiencesTableProps> = ({
  audiences,
  loading,
  error,
  paginationProps,
  isUserPermittedToEditAudience,
}) => {
  const navigate = useNavigate();

  const renderData = (items: PartialAudienceFragment[]) => {
    const rows = items.map(({ uuid, name, status, hasBeenBackFilled, targetingAttributes }) => {
      const onClick = () => navigate(routeCreator.details(uuid));

      return (
        <TableRow key={uuid} onClick={onClick}>
          <TableCell>{name || 'No Name'}</TableCell>
          <TableCell>
            <StatusChip
              label={getStatusLabel(
                status,
                hasBeenBackFilled,
                hasFixedDateRange(targetingAttributes),
              )}
              variant={getAudienceStatusVariant(status)}
            />
          </TableCell>
          {isUserPermittedToEditAudience && (
            <TableCell padding='button' align='right'>
              {status && <AudienceActionsContainer id={uuid} name={name || ''} status={status} />}
            </TableCell>
          )}
        </TableRow>
      );
    });

    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              {isUserPermittedToEditAudience && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<PartialAudienceFragment>
      error={error}
      loading={loading}
      items={audiences}
      renderData={renderData}
    />
  );
};
